import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, NEEDS_PROFILE } from '../../auth/auth';
import './SignInModal.css';
import { signInWithRedirect, confirmSignUp, resendSignUpCode } from 'aws-amplify/auth';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import {
  rememberReturnPath, clearReturnPath, resumeUrl, signInErrorMessage, confirmCodeErrorMessage, isAlreadyConfirmedError,
} from '../accountFlow';

const SignInModal = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmNotice, setConfirmNotice] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onCreateAccount = () => {
    navigate('/create-profile');
    onClose();
  };

  const finishSignIn = async () => {
    const result = await login(email, password);
    if (result === NEEDS_PROFILE) {
      navigate(resumeUrl());
      onClose();
      return true;
    }
    if (result) {
      onClose();
      return true;
    }
    setError("Couldn't log you in. Check your email and password.");
    return false;
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (isLoading) return;
    setError('');
    setNeedsConfirmation(false);

    if (!email) {
      setError('Please enter an email.');
      return;
    }

    if (!password) {
      setError('Please enter a password.');
      return;
    }

    setIsLoading(true);
    try {
      await finishSignIn();
    } catch (error) {
      if (error?.name === 'UserNotConfirmedException') setNeedsConfirmation(true);
      setError(signInErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const openCodeEntry = () => {
    setConfirmError('');
    setConfirmNotice('');
    setConfirmOpen(true);
  };

  const sendNewCode = async () => {
    await resendSignUpCode({ username: email });
  };

  const handleResendAndOpen = async () => {
    setConfirmError('');
    try {
      await sendNewCode();
      setConfirmNotice('We sent a new code to your email.');
    } catch (err) {
      setConfirmNotice('');
      setConfirmError(err?.message || 'Could not send a new code. Please try again.');
    }
    setConfirmOpen(true);
  };

  const handleConfirmCode = async (code) => {
    setConfirmError('');
    try {
      await confirmSignUp({ username: email, confirmationCode: code });
    } catch (err) {
      if (!isAlreadyConfirmedError(err)) {
        setConfirmError(confirmCodeErrorMessage(err));
        return { success: false };
      }
    }
    setConfirmLoading(true);
    try {
      const done = await finishSignIn();
      if (!done) setConfirmOpen(false);
    } catch (err) {
      setConfirmOpen(false);
      setNeedsConfirmation(false);
      setError(signInErrorMessage(err));
    } finally {
      setConfirmLoading(false);
    }
    return { success: true };
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  const handleGoogleSignIn = async () => {
    try {
      rememberReturnPath(`${window.location.pathname}${window.location.search}`);

      await signInWithRedirect({
        provider: 'Google',
        options: {
          prompt: 'consent select_account',
        }
      });

    } catch (error) {
      clearReturnPath();
      if (error.message && error.message.includes('signed in user')) {
        setError('Please refresh the page and try again.');
      } else {
        setError(error.message);
      }
    }
  };

  if (confirmOpen) {
    return (
      <ConfirmationModal
        isOpen
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmCode}
        username={email}
        notice={confirmNotice}
        errorMessage={confirmError}
        loading={confirmLoading}
        loadingTitle="Signing In"
        resendCode={sendNewCode}
      />
    );
  }

  return (
    <div className="spiggl-sign-in-modal-overlay">
      <div className="spiggl-sign-in-modal-content">
        <button className="spiggl-sign-in-close-button" onClick={onClose} aria-label="Close">×</button>
        <h3 className="spiggl-sign-in-header">Log In</h3>
        {error && <p className="spiggl-sign-in-error" role="alert">{error}</p>}
        {needsConfirmation && (
          <div className="spiggl-signin-note">
            <button type="button" className="spiggl-forgot-password-link" onClick={openCodeEntry} disabled={isLoading}>
              Enter your code
            </button>
            <button type="button" className="spiggl-forgot-password-link" onClick={handleResendAndOpen} disabled={isLoading}>
              Resend code
            </button>
          </div>
        )}
        <form onSubmit={handleSignIn} className="spiggl-sign-in-form">
          <div className="spiggl-sign-in-input-group">
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setNeedsConfirmation(false); }}
              placeholder=" "
              autoComplete="email"
              disabled={isLoading}
            />
            <label htmlFor="email">Email</label>
          </div>
          <div className="spiggl-sign-in-input-group">
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder=" "
              autoComplete="current-password"
              disabled={isLoading}
            />
            <label htmlFor="password">Password</label>
          </div>
          <button
            type="button"
            onClick={handleForgotPassword}
            className="spiggl-forgot-password-link"
            disabled={isLoading}
          >
            Forgot Password?
          </button>
          <button
            type="submit"
            className="spiggl-sign-in-button"
            disabled={isLoading}
          >
            {isLoading ? 'Signing In...' : 'Log In'}
          </button>
        </form>
        <div className="spiggl-sign-in-options">
          <div className="spiggl-create-account">
            <button
              onClick={onCreateAccount}
              className="spiggl-create-account-button"
              disabled={isLoading}
            >
              Create Profile
            </button>
          </div>
          <div className="spiggl-signin-divider">
            <span>or</span>
          </div>
          <button
            onClick={handleGoogleSignIn}
            className="spiggl-google-sign-in-button"
            type="button"
            disabled={isLoading}
          >
            <img src="/icons/google.png" alt="Google" className="spiggl-google-icon" />
            <span>Log In with Google</span>
          </button>
          <div className="spiggl-signin-note">
            <span>Contractor? Create a business profile first.</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignInModal;
